import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Plus, X, Play, Camera, Share2 } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const StoriesBar = ({ user }) => {
  const [storyGroups, setStoryGroups] = useState([]);
  const [activeStory, setActiveStory] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [showSharePrompt, setShowSharePrompt] = useState(false);
  const scrollRef = useRef(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchStories();
  }, []);

  const fetchStories = async () => {
    try {
      const res = await axios.get(`${API}/stories`);
      setStoryGroups(res.data || []);
    } catch (e) {
      console.log('No stories yet');
    }
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const token = localStorage.getItem('token') || localStorage.getItem('bar_owner_token');
    if (!token) return;

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('caption', '');

    try {
      await axios.post(`${API}/stories/upload`, formData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      fetchStories();
      toast.success('Story uploaded!');
      setShowSharePrompt(true);
    } catch (err) {
      console.error('Upload failed:', err);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const getProfilePic = (group) => {
    if (group.profile_picture_url) {
      const url = group.profile_picture_url;
      return url.startsWith('/api') ? `${BACKEND_URL}${url}` : url;
    }
    return null;
  };

  const openStory = (group) => {
    setActiveStory({ group, index: 0 });
  };

  const closeStory = () => setActiveStory(null);

  const nextStory = () => {
    if (!activeStory) return;
    const stories = activeStory.group.stories;
    if (activeStory.index < stories.length - 1) {
      setActiveStory({ ...activeStory, index: activeStory.index + 1 });
    } else {
      closeStory();
    }
  };

  return (
    <>
      {/* Stories Scroll Bar */}
      <div className="mb-6">
        <div
          ref={scrollRef}
          className="flex gap-3 overflow-x-auto pb-2 scrollbar-hide"
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
        >
          {/* Add Story Button */}
          <div className="flex flex-col items-center gap-1 flex-shrink-0">
            <button
              data-testid="add-story-btn"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className="w-16 h-16 rounded-full border-2 border-dashed border-indigo-500/60 flex items-center justify-center bg-slate-800/50 hover:bg-indigo-500/10 transition-all"
            >
              {uploading ? (
                <div className="w-5 h-5 border-2 border-indigo-400 border-t-transparent rounded-full animate-spin" />
              ) : (
                <Plus className="w-5 h-5 text-indigo-400" />
              )}
            </button>
            <span className="text-[10px] text-slate-400 whitespace-nowrap">Your Story</span>
            <input
              ref={fileInputRef}
              type="file"
              accept="video/*,image/*"
              onChange={handleUpload}
              className="hidden"
            />
          </div>

          {/* Story Circles */}
          {storyGroups.map((group) => (
            <div
              key={group.user_id}
              className="flex flex-col items-center gap-1 flex-shrink-0 cursor-pointer"
              onClick={() => openStory(group)}
              data-testid={`story-circle-${group.user_id}`}
            >
              <div className="w-16 h-16 rounded-full p-[2px] bg-gradient-to-tr from-amber-500 via-pink-500 to-purple-600">
                <div className="w-full h-full rounded-full bg-slate-900 p-[2px]">
                  {getProfilePic(group) ? (
                    <img
                      src={getProfilePic(group)}
                      alt={group.username}
                      className="w-full h-full rounded-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full rounded-full bg-gradient-to-br from-indigo-600 to-purple-600 flex items-center justify-center">
                      <span className="text-white text-sm font-bold">
                        {group.username?.charAt(0)?.toUpperCase() || '?'}
                      </span>
                    </div>
                  )}
                </div>
              </div>
              <span className="text-[10px] text-slate-300 max-w-16 truncate">
                {group.username || 'User'}
              </span>
              {group.user_type === 'bar_owner' && (
                <span className="text-[8px] text-amber-400 -mt-0.5">Bar Owner</span>
              )}
            </div>
          ))}

          {storyGroups.length === 0 && (
            <div className="flex items-center px-4 text-slate-500 text-sm">
              <Camera className="w-4 h-4 mr-2" />
              Be the first to post a story!
            </div>
          )}
        </div>
      </div>

      {/* Instagram Share Prompt */}
      {showSharePrompt && (
        <div className="fixed inset-0 z-[200] bg-black/70 flex items-center justify-center p-4" onClick={() => setShowSharePrompt(false)}>
          <div className="bg-slate-900 border border-slate-700 rounded-2xl p-6 max-w-sm w-full text-center" onClick={(e) => e.stopPropagation()}>
            <div className="w-14 h-14 mx-auto mb-3 rounded-full bg-gradient-to-tr from-amber-500 via-pink-500 to-purple-600 flex items-center justify-center">
              <Share2 className="w-7 h-7 text-white" />
            </div>
            <h3 className="text-white font-bold text-lg mb-1">Story Posted!</h3>
            <p className="text-slate-400 text-sm mb-4">Want to share it on Instagram too?</p>
            <div className="flex gap-2">
              <button
                data-testid="share-instagram-btn"
                onClick={() => {
                  window.open('https://www.instagram.com/', '_blank');
                  setShowSharePrompt(false);
                  toast.success('Opening Instagram — paste your story there!');
                }}
                className="flex-1 py-2.5 rounded-xl bg-gradient-to-r from-amber-500 via-pink-500 to-purple-600 text-white font-semibold text-sm hover:opacity-90 transition-all"
              >
                Share to Instagram
              </button>
              <button
                onClick={() => setShowSharePrompt(false)}
                className="px-4 py-2.5 rounded-xl bg-slate-800 text-slate-400 text-sm hover:text-white transition-all"
              >
                Skip
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Story Viewer Modal */}
      {activeStory && (
        <div
          className="fixed inset-0 z-[200] bg-black flex items-center justify-center"
          onClick={nextStory}
          data-testid="story-viewer"
        >
          {/* Close button */}
          <button
            onClick={(e) => { e.stopPropagation(); closeStory(); }}
            className="absolute top-4 right-4 z-[201] text-white/80 hover:text-white"
            data-testid="close-story-btn"
          >
            <X className="w-8 h-8" />
          </button>

          {/* Progress bar */}
          <div className="absolute top-2 left-4 right-4 flex gap-1 z-[201]">
            {activeStory.group.stories.map((_, i) => (
              <div key={i} className="flex-1 h-0.5 rounded-full overflow-hidden bg-white/20">
                <div
                  className={`h-full rounded-full transition-all duration-300 ${
                    i < activeStory.index ? 'bg-white w-full' :
                    i === activeStory.index ? 'bg-white w-full animate-pulse' :
                    'w-0'
                  }`}
                />
              </div>
            ))}
          </div>

          {/* User info */}
          <div className="absolute top-8 left-4 z-[201] flex items-center gap-2">
            {getProfilePic(activeStory.group) ? (
              <img src={getProfilePic(activeStory.group)} alt="" className="w-8 h-8 rounded-full object-cover" />
            ) : (
              <div className="w-8 h-8 rounded-full bg-indigo-600 flex items-center justify-center text-white text-xs font-bold">
                {activeStory.group.username?.charAt(0)?.toUpperCase() || '?'}
              </div>
            )}
            <span className="text-white text-sm font-medium">{activeStory.group.username || 'User'}</span>
          </div>

          {/* Media content */}
          {(() => {
            const story = activeStory.group.stories[activeStory.index];
            const mediaUrl = `${API.replace('/api', '')}/api/stories/media/${story.id}`;
            if (story.media_type === 'video') {
              return (
                <video
                  src={mediaUrl}
                  className="max-h-[85vh] max-w-full object-contain rounded-lg"
                  autoPlay
                  playsInline
                  onEnded={nextStory}
                  onClick={(e) => e.stopPropagation()}
                />
              );
            }
            return (
              <img
                src={mediaUrl}
                alt="Story"
                className="max-h-[85vh] max-w-full object-contain rounded-lg"
              />
            );
          })()}

          {/* Caption */}
          {activeStory.group.stories[activeStory.index]?.caption && (
            <div className="absolute bottom-8 left-4 right-4 z-[201]">
              <p className="text-white text-center bg-black/50 rounded-lg px-4 py-2">
                {activeStory.group.stories[activeStory.index].caption}
              </p>
            </div>
          )}

          {/* Tap zones */}
          <div className="absolute inset-0 flex z-[199]">
            <div className="w-1/3 h-full" onClick={(e) => {
              e.stopPropagation();
              if (activeStory.index > 0) setActiveStory({...activeStory, index: activeStory.index - 1});
            }} />
            <div className="w-2/3 h-full" onClick={nextStory} />
          </div>
        </div>
      )}
    </>
  );
};

export default StoriesBar;
